import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import { Application, ApplicationQuestion, Field, FieldType } from '@bybeam/platform-types';
import AnswerReview from '@platform-ui-common/components/AnswerReview/index.js';
import {
  ComplexFieldAnswer,
  RadioGroupAnswer,
} from '@platform-ui-common/components/AnswerReview/types.js';
import {
  getFieldAnswersValue,
  getUnhousedField,
} from '@platform-ui/utilities/application/fields/index.js';
import InfoField from './InfoField.js';

interface ReviewFieldProps {
  application: Application;
  field: Field;
  applicationQuestion: ApplicationQuestion;
}

export default function ReviewField({
  application,
  applicationQuestion,
  field,
}: ReviewFieldProps): JSX.Element {
  const sortedVersions = [...(application.versions ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const showDiff = sortedVersions.length > 1;
  const previousVersion = showDiff ? sortedVersions?.[sortedVersions.length - 2] : null;

  const fieldReviews = previousVersion?.mappedAnswers?.flatMap((section) => {
    return section.questionGroups.flatMap((questionGroup) => {
      return questionGroup.questions.flatMap((question) => {
        return question.fields
          .flatMap((previousVersionField) => {
            if (field.key !== previousVersionField.field?.key) return null;

            return previousVersionField.answer?.fieldReviewsRequiringResubmission?.length
              ? previousVersionField.answer
              : null;
          })
          .filter(Boolean);
      });
    });
  });

  switch (field.type) {
    case FieldType.CheckboxGroup:
    case FieldType.RadioList:
      return (
        <InfoField
          // biome-ignore lint/suspicious/noExplicitAny: copy does not exist on some type
          subtitle={(field as any).copy ?? applicationQuestion.copy.title}
          requiresResubmission={fieldReviews?.length}
        >
          <AnswerReview
            source={application}
            field={field}
            answer={getFieldAnswersValue<RadioGroupAnswer | string[]>(
              field.key,
              application.answers,
            )}
            showDiff={showDiff}
            previousAnswer={getFieldAnswersValue<RadioGroupAnswer | string[]>(
              field.key,
              previousVersion?.answers,
            )}
          />
        </InfoField>
      );

    case FieldType.Document:
      return (
        <InfoField
          subtitle={field.copy?.title ?? applicationQuestion.copy.title}
          requiresResubmission={fieldReviews?.length}
        >
          <AnswerReview
            source={application}
            field={field}
            answer={getFieldAnswersValue<RadioGroupAnswer | string[]>(
              field.key,
              application.answers,
            )}
            showDiff={showDiff}
            previousAnswer={getFieldAnswersValue<RadioGroupAnswer | string[]>(
              field.key,
              previousVersion?.answers,
            )}
          />
        </InfoField>
      );

    case FieldType.Checkbox:
      if (field.key.includes('attestation')) return null;
      return (
        <AnswerReview
          source={application}
          field={field}
          answer={getFieldAnswersValue<boolean>(field.key, application.answers)}
          showDiff={showDiff}
          previousAnswer={getFieldAnswersValue<boolean>(field.key, previousVersion?.answers)}
        />
      );
    case FieldType.Dropdown:
    case FieldType.Date:
    case FieldType.Text:
      return (
        <InfoField subtitle={field.copy} requiresResubmission={fieldReviews?.length}>
          <AnswerReview
            source={application}
            field={field}
            answer={getFieldAnswersValue<string | number | string[]>(
              field.key,
              application.answers,
            )}
            showDiff={showDiff}
            previousAnswer={getFieldAnswersValue<string | number | string[]>(
              field.key,
              previousVersion?.answers,
            )}
          />
        </InfoField>
      );
    case FieldType.Complex:
      return (
        <InfoField subtitle={field.copy} separator requiresResubmission={fieldReviews?.length}>
          <AnswerReview
            source={application}
            field={field}
            answer={getFieldAnswersValue<ComplexFieldAnswer>(field.key, application.answers)}
            showDiff={showDiff}
            previousAnswer={getFieldAnswersValue<ComplexFieldAnswer>(
              field.key,
              previousVersion?.answers,
            )}
          />
        </InfoField>
      );

    case FieldType.Address:
      return (
        <>
          {field.allowUnhoused && (
            <ReviewField
              application={application}
              applicationQuestion={applicationQuestion}
              field={getUnhousedField(field.key)}
            />
          )}
          <InfoField
            subtitle={field?.copy || applicationQuestion.copy.title}
            requiresResubmission={fieldReviews?.length}
          >
            <AnswerReview
              source={application}
              field={field}
              answer={getFieldAnswersValue<string>(`${field.key}Id`, application.answers)}
              showDiff={showDiff}
              previousAnswer={getFieldAnswersValue<string>(
                `${field.key}Id`,
                previousVersion?.answers,
              )}
            />
          </InfoField>
        </>
      );

    // biome-ignore lint/suspicious/noFallthroughSwitchClause: intentionally fall through for non-display
    case FieldType.Calculated:
      if (field.display)
        return (
          <InfoField subtitle={field.display.copy} requiresResubmission={fieldReviews?.length}>
            <AnswerReview
              source={application}
              field={field}
              answer={getFieldAnswersValue<string | number | string[]>(
                field.key,
                application.answers,
              )}
              showDiff={showDiff}
              previousAnswer={getFieldAnswersValue<string | number | string[]>(
                field.key,
                previousVersion?.answers,
              )}
            />
          </InfoField>
        );

    // Else fall through
    case FieldType.Typography:
      return null;

    default:
      assertExhaustive(field);
  }
}
